import { useEffect, useState, useMemo, useRef } from "react";
import { Flex, Select, SelectProps, Spin, Tag } from "antd";
import { useTranslation } from "react-i18next";

import api from "@/apis/supplier";
import { debounce } from "lodash";
import { CellSelectWrap } from "@/pages/Sales/style";
import { PAGE_SIZE } from "@/utils/const";
import { Supplier } from "@/types/purchase";
import SText from "@/styles/SText";
import { getSupplierName } from "@/utils/supplier";

interface SCurrencySelectProps extends SelectProps {
  onChangeCustomer?: (info: Supplier) => void;
  // 新增：允许外部传入供应商数据，避免调用 supplier/list 接口
  supplierData?: Array<Supplier>;
}

const mergeSuppliers = (...groups: Array<Array<Supplier> | undefined>) => {
  const supplierMap = new Map<string, Supplier>();

  groups.forEach(group => {
    group?.forEach(supplier => {
      if (supplier?.id) {
        supplierMap.set(supplier.id, supplier);
      }
    });
  });

  return Array.from(supplierMap.values());
};

const SupplierSelect = ({
  onChangeCustomer,
  onChange,
  supplierData,
  ...props
}: SCurrencySelectProps) => {
  const { t } = useTranslation();

  const [list, setList] = useState<Array<Supplier>>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(1);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // 如果传入了 supplierData，则使用外部数据，不需要调用 API
  const useExternalData = !!supplierData;
  const displayList = useExternalData ? supplierData : list;
  const selectedSupplierId =
    typeof props.value === "string" ? props.value : undefined;

  // 使用 useMemo 对供应商数据进行去重和排序
  const uniqueSuppliers = useMemo(() => {
    if (!useExternalData) return displayList;

    // 按 ID 去重
    const supplierMap = new Map();
    supplierData.forEach(supplier => {
      if (supplier && supplier.id) {
        supplierMap.set(supplier.id, supplier);
      }
    });

    // 按 code 或 name 排序
    return Array.from(supplierMap.values()).sort((a, b) => {
      const aKey = a.shortName || a.name1 || a.name2 || a.code || '';
      const bKey = b.shortName || b.name1 || b.name2 || b.code || '';
      return aKey.localeCompare(bKey);
    });
  }, [supplierData, useExternalData, displayList]);

  useEffect(() => {
    if (
      useExternalData ||
      !selectedSupplierId ||
      selectedSupplier?.id === selectedSupplierId ||
      list.some(supplier => supplier.id === selectedSupplierId)
    ) {
      return;
    }

    api.fetch(selectedSupplierId).then((supplier: any) => {
      if (supplier?.id) {
        setSelectedSupplier(supplier);
        setList(last => mergeSuppliers([supplier], last));
      }
    }).catch(() => { });
  }, [list, selectedSupplier?.id, selectedSupplierId, useExternalData]);

  const fetch = (keyword: string, current: number) => {
    setListLoading(true);
    api.list({
      body: keyword,
      pagination: { offset: (current - 1) * PAGE_SIZE, take: PAGE_SIZE },
    })
      .then((res) => {
        if (current === 1) {
          setList(
            mergeSuppliers(
              selectedSupplier ? [selectedSupplier] : undefined,
              res.body
            )
          );
        } else {
          setList(last => mergeSuppliers(last, res.body));
        }
        setTotal(res.pagination?.size ?? 0);
      })
      .finally(() => {
        setListLoading(false);
      });
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    // 使用外部数据时，不需要滚动加载
    if (useExternalData) return;

    const target = event.target as HTMLDivElement;
    if (
      currentPage * PAGE_SIZE < total &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight
    ) {
      let page = currentPage + 1;
      fetch(keyword, page);
      setCurrentPage(page);
    }
  };

  const onSearchRef = useRef(
    debounce((val: string, isExternal: boolean, fetchFn: (kw: string, page: number) => void) => {
      if (isExternal) {
        setKeyword(val.trim());
        return;
      }
      setKeyword(val.trim());
      setCurrentPage(1);
      fetchFn(val, 1);
    }, 500)
  );
  const onSearch = (val: string) => onSearchRef.current(val, useExternalData, fetch);

  // 前端过滤（当使用外部数据时）
  const filteredOptions = useMemo(() => {
    if (!useExternalData || !keyword) return uniqueSuppliers;

    const lowerKeyword = keyword.toLowerCase();
    return uniqueSuppliers.filter(supplier => {
      const searchFields = [
        supplier.code,
        supplier.shortName,
        supplier.name
      ];
      return searchFields.some(field =>
        field && field.toLowerCase().includes(lowerKeyword)
      );
    });
  }, [uniqueSuppliers, keyword, useExternalData]);

  return (
    <CellSelectWrap style={{ flex: 1 }}>
      <Select
        filterOption={false}
        onSearch={onSearch}
        popupMatchSelectWidth={false}
        onDropdownVisibleChange={(open) => {
          if (open) {
            onSearchRef.current.cancel();
            setKeyword("");
            if (!useExternalData) {
              setCurrentPage(1);
              fetch("", 1);
            }
          }
        }}
        onChange={(val, info) => {
          onSearchRef.current.cancel();
          setKeyword("");
          setSelectedSupplier(
            val && !Array.isArray(info) ? (info as Supplier) : null
          );
          onChange && onChange(val, info);
          onChangeCustomer && onChangeCustomer(info as Supplier)
        }}
        style={{ width: "100%" }}
        showSearch
        placeholder={t("purchase.plProductPrice")}
        fieldNames={{ value: 'id', label: 'code' }}
        optionRender={({ data }) =>
          <Flex style={{ width: '100%' }} align='center' justify='space-between'>
            <SText text={getSupplierName(data as Supplier)} />
            <Tag color='blue'>{data.code}</Tag>
          </Flex>
        }
        options={useExternalData ? filteredOptions : list}
        onPopupScroll={handleScroll}
        dropdownRender={(ele) => (
          <>
            {ele}
            {listLoading && (
              <Flex
                style={{ width: "100%", padding: 10 }}
                justify="center"
                align="center"
              >
                <Spin />
              </Flex>
            )}
          </>
        )}
        {...props}
      />
    </CellSelectWrap>
  );
};

export default SupplierSelect;
